import React, { useEffect, useState } from "react";
import "./AdminAdvertisers.css";
import "../admin/AdminDashboard.css";
import { db, storage } from "../firebase";

import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  orderBy,
  query,
} from "firebase/firestore";

import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";

export default function AdminAdvertisers() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [advertisers, setAdvertisers] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [name, setName] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [error, setError] = useState("");

  /* -----------------------------------------------------------
    LOAD ADVERTISERS
  ----------------------------------------------------------- */
  useEffect(() => {
    async function fetchAdvertisers() {
      try {
        const q = query(
          collection(db, "advertisers"),
          orderBy("createdAt", "desc")
        );

        const snap = await getDocs(q);
        const now = new Date();

        let list = snap.docs.map((d) => {
          const data = d.data();

          let isExpired = false;

          if (data.activeUntil) {
            const expiry = data.activeUntil.toDate
              ? data.activeUntil.toDate()
              : new Date(data.activeUntil);
            isExpired = expiry <= now;
          }

          return { id: d.id, ...data, isExpired };
        });

        // Sort so expired ads appear at the bottom
        list.sort((a, b) => {
          if (a.isExpired === b.isExpired) return 0;
          return a.isExpired ? 1 : -1; // expired → bottom
        });

        setAdvertisers(list);
      } catch (err) {
        console.error("Error loading advertisers:", err);
        setError("Failed to load advertisers.");
      } finally {
        setLoading(false);
      }
    }

    fetchAdvertisers();
  }, []);

  const resetForm = () => {
    setName("");
    setLinkUrl("");
    setImageFile(null);
  };

  /* -----------------------------------------------------------
    CREATE ADVERTISER (inactive by default)
  ----------------------------------------------------------- */
  const handleCreate = async (e) => {
    e.preventDefault();
    setError("");

    if (!name.trim()) return setError("Name is required.");
    if (!linkUrl.trim()) return setError("Link URL is required.");
    if (!imageFile) return setError("Please upload a banner image.");

    try {
      setSaving(true);

      const imgRef = ref(
        storage,
        `advertisers/${Date.now()}_${imageFile.name}`
      );

      await uploadBytes(imgRef, imageFile);
      const imageUrl = await getDownloadURL(imgRef);

      const docRef = await addDoc(collection(db, "advertisers"), {
        name,
        linkUrl,
        imageUrl,
        isActive: false, // starts inactive
        createdAt: serverTimestamp(),
      });

      setAdvertisers((prev) => [
        {
          id: docRef.id,
          name,
          linkUrl,
          imageUrl,
          isActive: false,
          isExpired: false,
        },
        ...prev,
      ]);

      resetForm();
    } catch (err) {
      console.error("Error creating advertiser:", err);
      setError("Failed to create advertiser.");
    } finally {
      setSaving(false);
    }
  };

  /* -----------------------------------------------------------
    DELETE ADVERTISER
  ----------------------------------------------------------- */
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this advertiser?"))
      return;

    try {
      await deleteDoc(doc(db, "advertisers", id));
      setAdvertisers((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      console.error("Error deleting advertiser:", err);
      setError("Failed to delete advertiser.");
    }
  };

  /* -----------------------------------------------------------
    RENDER PAGE
  ----------------------------------------------------------- */
  return (
    <div className="admin-page">
      <h1>Advertisers</h1>
      <p className="admin-subtitle">Manage advertiser banners & links</p>

      {/* ⭐ ADDED WRAPPER — REQUIRED FOR SCOPED CSS */}
      <div className="admin-advertisers-page">

        {/* ADD NEW ADVERTISER */}
        <div className="section-box">
          <h2 className="section-title">Add New Advertiser</h2>

          {error && (
            <p style={{ color: "#ff8fb4", marginBottom: "10px" }}>{error}</p>
          )}

          <form
            className="admin-form-large advertiser-form"
            onSubmit={handleCreate}
          >
            <div>
              <label className="admin-label">Name</label>
              <input
                type="text"
                className="admin-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Local Cafe"
              />
            </div>

            <div>
              <label className="admin-label">Link URL</label>
              <input
                type="url"
                className="admin-input"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
              />
            </div>

            <div>
              <label className="admin-label">Banner Image</label>
              <input
                type="file"
                className="admin-input"
                accept="image/*"
                onChange={(e) => setImageFile(e.target.files[0] || null)}
              />
            </div>

            <button className="admin-button" type="submit" disabled={saving}>
              {saving ? "Saving…" : "Create Advertiser"}
            </button>
          </form>
        </div>

        {/* EXISTING ADVERTISERS */}
        <section className="admin-section">
          <h2>Existing Advertisers</h2>
          <p className="admin-section-subtitle">
            Active ads at the top • Expired ads highlighted & sorted at bottom
          </p>

          {loading ? (
            <p className="admin-empty">Loading advertisers…</p>
          ) : advertisers.length === 0 ? (
            <p className="admin-empty">No advertisers yet.</p>
          ) : (
            <ul className="advertiser-list">
              {advertisers.map((ad) => (
                <li
                  key={ad.id}
                  className="advertiser-list-item"
                  style={{
                    background: ad.isExpired
                      ? "rgba(255,0,0,0.12)"
                      : "rgba(255,255,255,0.05)",
                    border: ad.isExpired
                      ? "2px solid rgba(255,0,0,0.4)"
                      : "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  {/* Thumbnail */}
                  <img
                    src={ad.imageUrl}
                    alt={ad.name}
                    className="advertiser-thumb"
                    onClick={() => setPreviewImage(ad.imageUrl)}
                  />

                  {/* Info */}
                  <div className="advertiser-info">
                    <strong>{ad.name}</strong>

                    <a
                      href={ad.linkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="advertiser-link"
                    >
                      {ad.linkUrl}
                    </a>

                    {ad.isExpired ? (
                      <span className="admin-pill danger">EXPIRED</span>
                    ) : (
                      <span
                        className={`admin-pill ${
                          ad.isActive ? "paid" : "free"
                        }`}
                      >
                        {ad.isActive ? "Active" : "Inactive"}
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="advertiser-actions">
                    <button
                      className="admin-button secondary"
                      onClick={() =>
                        navigate(`/admin/advertiser-edit/${ad.id}`)
                      }
                    >
                      Edit
                    </button>

                    <button
                      className="admin-button danger"
                      onClick={() => handleDelete(ad.id)}
                    >
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* IMAGE PREVIEW MODAL */}
        {previewImage && (
          <div className="ad-modal" onClick={() => setPreviewImage(null)}>
            <img src={previewImage} alt="Preview" className="ad-modal-image" />
          </div>
        )}
      </div>
      {/* END WRAPPER */}
    </div>
  );
}
